import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Button, Stack, Typography } from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";

import { AppGlobalContext } from "contexts/GlobalContext";
import { ModalConfirmEventos_ServicesOperation } from "views/Components/ModalConfirmEventos_Services_Operation";
import { Alerta_Permisos_Services_Operation } from "views/Components/Alerta_Permisos_Services_Operation";
import Animation_Success_animation from "../Helper/Animation_Success_animation";
import Animation_Warning_animation from "../Helper/Animation_Warning";

import useTicketDetail from "../Queries/useTicketDetailQuery";
import useFormLookups from "../Queries/useFormLookupsQuery";
import useStateData from "../Queries/stateDataQuery";
import { useAdminFormSubmit } from "./hooks/useAdminFormSubmit";

import Admin_Form_Header from "./Admin_Form_Header";
import Admin_Form_Body from "./Admin_Form_Body";
import Admin_Form_Body_Procesado from "./Admin_Form_Body_Procesado";
import Admin_Form_Body_Taller from "./Admin_Form_Body_Taller";
import Admin_Form_Body_finalizado from "./Admin_Form_Body_Finalizado";

import "../servicesOperation.css";
import { SkeletonsAdminForm } from "./SkeletonsAdminForm";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Mapa de componentes por estado: evita el renderizado condicional en cadena
const FORM_BY_STATE = {
  1: Admin_Form_Body,
  2: Admin_Form_Body_Procesado,
  3: Admin_Form_Body_Taller,
};

const Admin_Form = () => {
  const { mainCountry } = useContext(AppGlobalContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const parsedId = parseInt(id, 10);
  const isValidId = !isNaN(parsedId);

  // Estado local solo para datos editables por el usuario y UI
  const [formData, setFormData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [successModal, setSuccessModal] = useState({
    open: false,
    isReopened: false,
  });

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: ticketData, isLoading: ticketLoading } = useTicketDetail({
    ticketId: isValidId ? parsedId : null,
    countryId: mainCountry?.id,
  });

  const { data: lookups, isLoading: lookupsLoading } = useFormLookups({
    countryId: mainCountry?.id,
  });

  const { data: stateData } = useStateData({ countryId: mainCountry?.id });

  const isLoading = ticketLoading || lookupsLoading;

  // ticketData === null → el servidor devolvió array vacío (ticket no existe)
  const ticketNotFound = !ticketLoading && ticketData === null;

  // ── Sincronizar formData con los datos del ticket ─────────────────────────
  useEffect(() => {
    if (ticketData) setFormData(ticketData);
  }, [ticketData]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const displayStatus =
    stateData?.find((s) => String(s.id) === String(formData.estado))
      ?.nombre_estado ?? "Cargando...";

  // ── Submit ────────────────────────────────────────────────────────────────
  const { handleSubmit, isSubmitting, isSubmitSuccessful } = useAdminFormSubmit(
    {
      formData,
      onSuccess: (_, transition) => {
        // Invalidar caché del ticket y de la tabla para reflejar el nuevo estado
        queryClient.invalidateQueries({
          queryKey: [
            "serviceOperation",
            "ticketDetail",
            { ticketId: parsedId, countryId: mainCountry?.id },
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ["serviceOperation", "adminData"],
        });
        setSuccessModal({ open: true, isReopened: transition.nextState === 2 });
      },
      onError: () =>
        setSnackbar({ open: true, message: "Error en la ingesta de datos" }),
    },
  );

  // ── Handlers UI ───────────────────────────────────────────────────────────
  const handleBackRoute = (e) => {
    e.preventDefault();
    navigate("/service-operation/admin");
  };

  const handleCloseSuccess = () => navigate("/service-operation/admin");

  const handleChangeSku = (sku) =>
    setFormData((prev) => ({ ...prev, publicaciones_sku: sku }));

  const handleHeaderDataChange = (fieldName, value) =>
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

  // ── Mapeo de lookups a los nombres de props que esperan los hijos ─────────
  // (evita renombrar props en los componentes hijos)
  const {
    condicion: dataCondicion = [],
    deposito_destino: dataDeposito_destino = [],
    origen_devolucion: dataOrigen_devolucion = [],
    sintoma: dataSintoma = [],
    aceptamos_devolucion: dataAceptamos_Dev = [],
    taller_responsable: datataller_responsable = [],
    taller_lote: datataller_lote = [],
    taller_deposito_destino: datataller_deposito_destino = [],
    taller_componente: datataller_componente = [],
    taller_descripcion: datataller_descripcion = [],
    taller_estado_sku: datataller_estado_sku = [],
    taller_estetica_packaging: datataller_estetica_packaging = [],
    taller_estetica_producto: datataller_estetica_producto = [],
    taller_falla: datataller_falla = [],
    taller_motivo_del_scrap: datataller_motivo_del_scrap = [],
    taller_reparacion: datataller_reparacion = [],
  } = lookups ?? {};

  // Props compartidas por todos los formularios de estado
  const sharedBodyProps = {
    formData,
    setFormData,
    isLoading,
    isSubmitting,
    isSubmitSuccessful,
    handleSubmit,
    dataCondicion,
    dataDeposito_destino,
    dataOrigen_devolucion,
    dataSintoma,
    dataAceptamos_Dev,
    datataller_deposito_destino,
  };

  const FormComponent =
    FORM_BY_STATE[formData.estado] ?? Admin_Form_Body_finalizado;

  // ── Render ────────────────────────────────────────────────────────────────
  if (!isValidId || ticketNotFound) {
    return (
      <Alerta_Permisos_Services_Operation
        open
        CerrarModal={() => navigate("/service-operation/admin")}
        icon={<Animation_Warning_animation />}
        message="¡Ups! Hubo un error"
        message2={
          !isValidId
            ? "El ID que está buscando no es válido"
            : "No se encontraron datos para el ID proporcionado."
        }
      />
    );
  }

  return (
    <main className="main_container_admin" style={{ position: "relative" }}>
      {/* Cabecera: botón volver + título + estado */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="flex-start"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Button
          variant="contained"
          sx={{
            maxWidth: "50px",
            maxHeight: "50px",
            minWidth: "50px",
            minHeight: "50px",
            borderRadius: "8px",
            backgroundColor: "#002878",
            "&:hover": { backgroundColor: "#002878" },
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleBackRoute}
        >
          <ArrowBackIosNewOutlinedIcon sx={{ width: "20px", height: "20px" }} />
        </Button>
        <Stack
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          spacing={1}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "32px",
              color: "#000000DE",
              lineHeight: "24px",
            }}
          >
            Ticket #{id}
          </Typography>
          <Typography variant="h5">SOA · {displayStatus}</Typography>
        </Stack>
      </Stack>

      {/* Breadcrumb */}
      <Stack direction="row" alignItems="center" spacing={1} mt={2}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LayersOutlinedIcon
            sx={{ width: "24px", height: "24px", fill: "#00000099" }}
          />
          <Typography>Reparaciones</Typography>
        </Stack>
        <ArrowForwardIosOutlinedIcon
          sx={{ width: "16px", height: "16px", fill: "#0000008F" }}
        />
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#000000DE",
            lineHeight: "24px",
          }}
        >
          Ticket
        </Typography>
      </Stack>

      {/* Cuerpo */}
      <div>
        {isLoading && <SkeletonsAdminForm />}
        <main style={{ marginTop: "20px" }}>
          <form onSubmit={handleSubmit}>
            <Admin_Form_Header
              formData={formData}
              handleChangeSku={handleChangeSku}
              isLoading={isLoading}
              dataOrigen_devolucion={dataOrigen_devolucion}
              onHeaderDataChange={handleHeaderDataChange}
            />
            {/* Formulario de Pendiente de Ingreso */}
            {formData.estado === 1 && (
              <Admin_Form_Body
                {...sharedBodyProps}
                dataTecnico_responsable={datataller_responsable}
                dataLote={datataller_lote}
                setDataTaller_deposito_destino={() => {}}
              />
            )}
            {formData.estado === 2 && (
              <Admin_Form_Body_Procesado
                {...sharedBodyProps}
                dataTecnico_responsable={datataller_responsable}
                dataLote={datataller_lote}
                setDataTaller_deposito_destino={() => {}}
              />
            )}
            {formData.estado === 3 && (
              <Admin_Form_Body_Taller
                {...sharedBodyProps}
                datataller_componente={datataller_componente}
                datataller_descripcion={datataller_descripcion}
                datataller_estado_sku={datataller_estado_sku}
                datataller_estetica_packaging={datataller_estetica_packaging}
                datataller_estetica_producto={datataller_estetica_producto}
                datataller_falla={datataller_falla}
                datataller_lote={datataller_lote}
                datataller_motivo_del_scrap={datataller_motivo_del_scrap}
                datataller_reparacion={datataller_reparacion}
                datataller_responsable={datataller_responsable}
              />
            )}
            {(formData.estado === 4 ||
              (formData.estado !== 1 &&
                formData.estado !== 2 &&
                formData.estado !== 3)) && (
              <Admin_Form_Body_finalizado {...sharedBodyProps} />
            )}
          </form>
        </main>
      </div>

      {/* Snackbar de error */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de éxito */}
      <ModalConfirmEventos_ServicesOperation
        open={successModal.open}
        success={true}
        CerrarModal={handleCloseSuccess}
        icon={<Animation_Success_animation color="#2E7D32" />}
        message={
          successModal.isReopened
            ? "Se actualizó el estado del ticket correctamente!"
            : "¡Datos subidos correctamente!"
        }
        message2={`El código de seguimiento es: "N°${id}"`}
      />
    </main>
  );
};

export default Admin_Form;
